import { Fragment, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import Tooltip from 'app/components/Tooltip';
import { partitionArray } from 'app/utils/array';
import { getStatWinner } from 'app/utils/entities/match_data';
import LineGauge from 'app/components/Gauges/LineGauge';
import ThemeContext from 'app/common/ThemeContext';

import style from './style.module.scss';

interface Props<T> extends ClassNameProp {
  lines: GaugeStatLine<T>[];
  translationKey: string;
  forceGaugesDisplay?: boolean;
  joined?: boolean;
}

const StatLineGauge = <T extends object>({
  lines,
  translationKey,
  className,
  forceGaugesDisplay = false,
  joined = false
}: Props<T>) => {
  const { designVersion } = useContext(ThemeContext);

  const { t } = useTranslation();

  const lineGroups = partitionArray(lines, line => (line.absoluteValue ? 1 : 0));

  return (
    <>
      {lineGroups.map((lineGroup, index) => (
        <div
          className={classNames(
            style.statsContainer,
            { [style.statsContainerGrad]: designVersion === 'grad' },
            className
          )}
          key={index}
        >
          {lineGroup.map(line => {
            const {
              team1Value,
              team2Value,
              name,
              variation,
              absoluteValue,
              hideValue,
              displayFunction
            } = line;
            const key = `${translationKey}_${name.replaceAll('_', '-')}`;
            const statsWinners = getStatWinner(line);
            return (
              <Fragment key={key}>
                {designVersion === 'grad' && (
                  <div className={style.lineTitle}>{t(`${key}_title`)}</div>
                )}
                <div className={style.lineWrapper}>
                  <LineGauge
                    value={team1Value}
                    weak={!statsWinners[0]}
                    absoluteValue={absoluteValue}
                    hideValue={hideValue}
                    variation={designVersion === 'grad' ? 'grad_team_1' : variation}
                    displayFunction={displayFunction}
                    forceGaugeDisplay={forceGaugesDisplay}
                    joined={joined}
                  />
                  {designVersion !== 'grad' && (
                    <Tooltip content={t(`${key}_tooltip`)} className={style.statName}>
                      {t(`${key}_title`)}
                    </Tooltip>
                  )}
                  <LineGauge
                    value={team2Value}
                    weak={!statsWinners[1]}
                    absoluteValue={absoluteValue}
                    hideValue={hideValue}
                    variation={designVersion === 'grad' ? 'grad_team_2' : variation}
                    displayFunction={displayFunction}
                    orientation="reversed"
                    forceGaugeDisplay={forceGaugesDisplay}
                    joined={joined}
                  />
                </div>
              </Fragment>
            );
          })}
        </div>
      ))}
    </>
  );
};

StatLineGauge.displayName = 'StatLineGauge';

export default StatLineGauge;
